/**
 * Helpers for recognizing language codes that may arrive in either
 * ISO-639-1 ('ES' / 'EN') or ISO-639-2/B ('SPA' / 'ENG') form.
 *
 * Backends (e.g. Telex) sometimes emit one or the other depending on
 * the data source. These helpers let UI code treat each pair as the
 * same language for label, toggle, and equality decisions without
 * having to repeat the variant lists at each call site.
 */

import { LANGUAGE_TYPE } from '@constants/common/languageTypes';

export const SPANISH_LANGUAGE_CODES: ReadonlySet<string> = new Set([ 'ES', 'SPA' ]);
export const ENGLISH_LANGUAGE_CODES: ReadonlySet<string> = new Set([ 'EN', 'ENG' ]);

export const isSpanish = (code: string | undefined | null): boolean =>
    SPANISH_LANGUAGE_CODES.has(code ?? '');

export const isEnglish = (code: string | undefined | null): boolean =>
    ENGLISH_LANGUAGE_CODES.has(code ?? '');

/**
 * Returns true when both codes refer to the same language family
 * (e.g. 'SPA' and 'ES' are considered the same language, as are
 * 'ENG' and 'EN'). Falls back to strict equality for any code outside
 * the recognized Spanish/English pairs.
 */
export const isSameLanguage = (
    a: string | undefined | null,
    b: string | undefined | null
): boolean =>
    a === b ||
    (isSpanish(a) && isSpanish(b)) ||
    (isEnglish(a) && isEnglish(b));

/**
 * Maps a language code to the value for the <html lang> attribute, so screen
 * readers pick the right voice synthesizer.
 *
 * Anything that is not Spanish resolves to 'en', which keeps the dev-only
 * 'debug' locale and a not-yet-loaded environment out of the attribute.
 */
export const toHtmlLang = (code: string | undefined | null): LANGUAGE_TYPE =>
    isSpanish((code ?? '').toUpperCase()) ? 'es' : 'en';
